import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FormProvider, useForm, SubmitHandler, useWatch } from 'react-hook-form';

import MainContainerWithWindow from '../../../../../../components/MainContainerWithWindow/MainContainerWithWindow';
import { Loading } from '../../../../../../components/Loading/Loading';
import { getRequestErrorMessage, pushNotify } from '../../../../../../components/Notifications';
import { useCreateOrderMutation } from '../../../../../../redux/services/MerchantApi';
import { useGetCurrenciesQuery } from '../../../../../../redux/services/CurrencyApi';
import { ControlledInput } from '../../../../../../components/ControlledInput/ControlledInput';
import { Selector, TItem } from '../CreateReport/SelectType';
import styles from './CreateOrder.module.css';
import { sanitizeCurrencyAmount, validateCurrencyAmount } from '../../../../../../utils/functions/currencyAmount';

interface TInputs {
  amount: string;
  currency: string;
  requisite_type: 'C2C' | 'SBP' | 'TRANSFER';
}

export const CreateOrder: React.FC<{ open: boolean; onClose: () => void; onSuccess?: () => void }> = ({ open, onClose, onSuccess }) => {
  const [createOrder, { isLoading: isCreating }] = useCreateOrderMutation();
  const { data: currenciesData, isLoading: isCurrencyLoading, isError: isCurrencyError, refetch: refetchCurrencies } =
    useGetCurrenciesQuery('order', { skip: !open, refetchOnMountOrArgChange: true });
  const currencies = Array.isArray(currenciesData) ? currenciesData : [];
  const hasInvalidCurrencyData = currenciesData !== undefined && !Array.isArray(currenciesData);

  const methods = useForm<TInputs>({
    mode: 'onBlur',
    defaultValues: { amount: '', currency: '', requisite_type: 'C2C' },
  });
  const { handleSubmit, setValue, reset } = methods;
  const amountValue = useWatch({ control: methods.control, name: 'amount' });
  const currencyCode = useWatch({ control: methods.control, name: 'currency' });
  const paymentMethod = useWatch({ control: methods.control, name: 'requisite_type' });

  const selectedCurrency = currencies.find((currency) => currency.code === currencyCode);
  const methodOptions: TItem<'C2C' | 'SBP' | 'TRANSFER'>[] = [];
  if (selectedCurrency?.payment_methods.includes('SBP')) methodOptions.push({ id: 'SBP', name: 'СБП' });
  if (selectedCurrency?.payment_methods.includes('C2C')) methodOptions.push({ id: 'C2C', name: 'Банковская карта' });
  if (selectedCurrency?.payment_methods.includes('TRANSFER')) methodOptions.push({ id: 'TRANSFER', name: 'Перевод' });

  useEffect(() => {
    if (!currencyCode && currencies[0]) {
      setValue('currency', currencies[0].code, { shouldValidate: true });
    }
  }, [currencies, currencyCode, setValue]);
  useEffect(() => {
    if (methodOptions[0]) {
      setValue(
        'requisite_type',
        methodOptions[0].id,
        { shouldValidate: true },
      );
    }
  }, [selectedCurrency, setValue]);

  const currencyOptions = currencies.map((item) => ({
    id: item.code,
    name: `${item.code} · ${item.name}`,
  } as TItem<string>));

  const onSubmit: SubmitHandler<TInputs> = async (data) => {
    const amount = data.amount.replace(/\s/g, '');
    const amountError = validateCurrencyAmount(amount, selectedCurrency);
    if (amountError) {
      pushNotify(amountError, 'Error');
      return;
    }

    try {
      await createOrder({
        expected_income: amount,
        currency_code_from: 'USDT',
        currency_code_to: data.currency,
        requisite_type: data.requisite_type,
      }).unwrap();
      pushNotify('Заявка на пополнение успешно создана', 'Success');
      onSuccess?.();
      onClose();
      reset();
    } catch (error) {
      pushNotify(getRequestErrorMessage(error, 'Ошибка при создании заявки на пополнение'), 'Error');
    }
  };

  if (!open) return null;
  if (isCreating || isCurrencyLoading) {
    return createPortal(
      <MainContainerWithWindow>
        <Loading />
      </MainContainerWithWindow>,
      document.body
    );
  }

  if (isCurrencyError || hasInvalidCurrencyData) {
    return createPortal(
      <>
        <div className={styles.backdrop} />
        <section className={styles.modal} role="alertdialog" aria-modal="true" aria-label="Ошибка загрузки валют">
          <p>Не удалось загрузить доступные валюты.</p>
          <div className={styles.errorActions}>
            <button type="button" className={styles.secondaryButton} onClick={() => { onClose(); reset(); }}>Закрыть</button>
            <button type="button" className={styles.button} onClick={() => refetchCurrencies()}>Повторить</button>
          </div>
        </section>
      </>,
      document.body,
    );
  }

  const portal = (node: React.ReactNode) => createPortal(node, document.body);

  return portal(
    <FormProvider {...methods}>
      <div className={styles.backdrop} onClick={() => { onClose(); reset(); }} />
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="create-order-title" onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 id="create-order-title" className={styles.title}>Новая заявка на пополнение</h2>
          <button type="button" aria-label="Закрыть" className={styles.closeBtn} onClick={() => { onClose(); reset(); }}>×</button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label}>Сумма ({currencyCode || '—'})</label>
            <ControlledInput
              name="amount"
              placeholder="Введите сумму"
              rules={{ required: true }}
              transform={(text) =>
                text
                  ? sanitizeCurrencyAmount(text, selectedCurrency?.amount_scale)
                  : text
              }
              errorText="Некорректная сумма"
            />
          </div>
          <Selector
            title="Валюта"
            items={currencyOptions}
            value={currencyCode}
            onChange={(v) => setValue('currency', v, { shouldValidate: true })}
          />
          <Selector
            title="Метод оплаты"
            items={methodOptions}
            value={paymentMethod}
            onChange={(v) => setValue('requisite_type', v, { shouldValidate: true })}
          />
          <div className={styles.actions}>
          <button
            type="submit"
            className={styles.button}
            disabled={!amountValue || !currencyCode || !paymentMethod}
          >
            Создать
          </button>
          </div>
        </form>
      </div>
    </FormProvider>
  );
};
